import {
  formatAccountNumber,
  formatBodyType,
  formatCabType,
  formatEngineMake,
  formatMake,
  formatNumber,
  formatSourceDivision,
  formatStatus,
  formatTitleCase,
  formatTransmissionType,
  formatUnitModelClass,
  parseRangeQuery,
} from "../../../helpers/format";
import {
  SITE_DISTANCE_OPTIONS,
  SITE_SORT_OPTIONS,
} from "../../../constants/search";

export default function getBreadcrumbs(
  query,
  baseQuery,
  sliderDefaults,
  formType,
  authenticated,
  getCategoryNames
) {
  if (typeof baseQuery === "string") {
    baseQuery = JSON.parse(baseQuery);
  }

  const breadcrumbs = breadcrumbsInit.reduce(
    (bu, bc) => {
      const userDefined = query.hasOwnProperty(bc.param);
      const baseDefined = baseQuery.hasOwnProperty(bc.param);
      let value = userDefined && query[bc.param];

      if (!value && baseDefined) {
        value = baseQuery[bc.param];
      }

      // NOTE: this is a dirty, dirty hack
      // We must asynchronously fetch category names for the category_id breadcrumb,
      // but making the entire function async would require a lot of refactoring,
      // so we're cheating by updating an existing object reference in the
      // asynchronous function.
      if (
        !bc.hide &&
        (!bc.excludeFind ||
          (!!bc.excludeFind &&
            (!bc.excludeFind.includes(formType) ||
              (!authenticated && bc.excludeFind.includes("anonymous")))))
      ) {
        bu.find.push({
          ...bc,
          value: bc.fallbackValue,
        });
      }

      const handleCrumb = async v => {
        const formatted = await getBreadcrumbValue(
          bc.param,
          v,
          query,
          sliderDefaults,
          getCategoryNames
        );

        const crumb = { ...bc, orig: v, value: formatted };

        if (userDefined) {
          bu.user.push(crumb);
        }

        if (userDefined && !bc.hide) {
          bu.visible.push(crumb);
        }

        if ((userDefined || baseDefined) && !bc.hide) {
          const existing = bu.find.find(({ key }) => key === bc.key);
          if (existing && existing.value === bc.fallbackValue && formatted) {
            existing.value = formatted;
          } else if (existing && formatted) {
            existing.value += `, ${formatted}`;
          }
        }
      };

      if (bc.type === "range") {
        handleCrumb(value);
      } else {
        if (!Array.isArray(value)) {
          value = [value];
        }

        value.forEach(handleCrumb);
      }

      return bu;
    },
    { find: [], user: [], visible: [] }
  );

  return Object.values(breadcrumbs);
}

export const breadcrumbsInit = [
  {
    key: "Account Number",
    param: "accountNumber",
    type: "multiple",
    excludeFind: [
      "all",
      "anonymous",
      "attachments",
      "construction-equipment",
      "cranes",
      "trucks",
      "water-trucks-tanks",
      "watched",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Location",
    param: "locationZip",
    type: "single",
    excludeFind: [],
    fallbackValue: "Any",
  },
  {
    key: "Meter",
    param: "meter",
    hideKey: true,
    type: "range",
    excludeFind: ["all", "attachments", "construction-equipment", "trailers"],
    fallbackValue: "Any",
  },
  {
    key: "Model",
    param: "model",
    type: "multiple",
    excludeFind: [],
    fallbackValue: "Any",
  },
  {
    key: "Keyword",
    param: "search",
    type: "multiple",
    excludeFind: [],
    fallbackValue: "None",
  },
  {
    key: "Status",
    param: "status",
    type: "multiple",
    excludeFind: ["anonymous"],
    fallbackValue: "Any",
  },
  {
    key: "Page",
    hide: true,
    param: "page",
    type: "single",
  },
  {
    key: "Price",
    value: "Any",
    param: "price",
    type: "range",
    excludeFind: [],
    fallbackValue: "Any",
  },
  {
    key: "Sort",
    hide: false,
    hideDesktop: true,
    hideKey: true,
    param: "sort",
    type: "single",
  },
  {
    key: "Source Division",
    param: "sourceDivision",
    hideKey: true,
    type: "multiple",
    excludeFind: [],
    fallbackValue: "All",
  },
  {
    key: "Source Type",
    param: "sourceType",
    type: "single",
    excludeFind: [],
    fallbackValue: "All",
  },
  {
    key: "Make",
    param: "make",
    type: "multiple",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Visibility",
    param: "visibility",
    type: "single",
    excludeFind: ["anonymous"],
    fallbackValue: "All",
  },
  {
    key: "Year",
    param: "year",
    type: "range",
    excludeFind: ["all", "attachments"],
    fallbackValue: "Any",
  },
  {
    key: "Body Type",
    param: "bodyType",
    type: "multiple",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Class",
    param: "unitModelClass",
    type: "multiple",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Wheel Base",
    param: "wheelbase",
    type: "range",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Gross Vehicle Weight",
    param: "grossVehicleWeight",
    type: "range",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Cab Over",
    param: "isCabover",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Cab Extender",
    param: "hasCabExtenders",
    type: "single",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Cab Type",
    param: "cabType",
    type: "multiple",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Category",
    param: "category_id",
    type: "multiple",
    fallbackValue: "Any",
  },
  {
    key: "Beds",
    param: "sleeperNumberOfBeds",
    type: "multiple",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Sleeper Size",
    param: "sleeperSize",
    type: "range",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Rear Axles",
    param: "numberOfRearAxles",
    type: "multiple",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Front Rating",
    param: "frontAxleRating",
    type: "range",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Rear Rating",
    param: "rearAxleRating",
    type: "range",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Engine Make",
    param: "engineMake",
    type: "multiple",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Horsepower",
    param: "horsepower",
    type: "range",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Transmission",
    param: "transmissionType",
    type: "multiple",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
  {
    key: "Transmission Speed",
    param: "transmissionSpeed",
    type: "range",
    excludeFind: [
      "all",
      "attachments",
      "construction-equipment",
      "cranes",
      "trailers",
      "water-trucks-tanks",
    ],
    fallbackValue: "Any",
  },
];

async function getBreadcrumbValue(
  param,
  value,
  query,
  sliderDefaults,
  getCategoryNames
) {
  switch (param) {
    case "accountNumber":
      return !!value ? formatAccountNumber(value) : "Any";
    case "bodyType":
      return !!value ? formatBodyType(value) : "Any";
    case "cabType":
      return !!value ? formatCabType(value) : "Any";
    case "category_id":
      const formatted =
        !!value && !!getCategoryNames ? await getCategoryNames(value) : value;
      return !!value ? formatted : "Any";
    case "engineMake":
      return !!value ? formatEngineMake(value) : "Any";
    case "frontAxleRating":
    case "grossVehicleWeight":
    case "horsepower":
    case "rearAxleRating":
    case "sleeperSize":
    case "transmissionSpeed":
    case "wheelbase":
      return getAboveBelowRangeValue(value, sliderDefaults[param]) || "Any";
    case "hasCabExtenders":
    case "isCabover":
      return !!value ? "Required" : "Not Required";
    case "locationZip":
      return !!value ? getLocationZipValue(value, query?.distance) : "Any";
    case "make":
      return !!value ? formatMake(value) : "All";
    case "meter":
      return getMeterValue(value, sliderDefaults?.meter) || "Any";
    case "model":
    case "search":
      return !!value ? `${value}` : "Any";
    case "page":
      return `Page ${value || "1"}`;
    case "price":
      return getPriceValue(value, sliderDefaults?.price) || "Any";
    case "numberOfRearAxles":
    case "sleeperNumberOfBeds":
      const numberList = !!value
        ? `${value}`
            .split(",")
            .map(v => parseInt(v, 10))
            .filter(v => !isNaN(v))
        : [];
      return !!numberList.length ? numberList.join(", ") : "Any";
    case "sort":
      return `Sorted by ${getSortValue(value)}`;
    case "sourceDivision":
      return !!value ? formatSourceDivision(value) : "All";
    case "sourceType":
      return !!value ? `${formatTitleCase(value)} Equipment` : "All";
    case "status":
      return !!value ? formatStatus(value) : "All";
    case "transmissionType":
      return !!value ? formatTransmissionType(value) : "Any";
    case "unitModelClass":
      return !!value ? formatUnitModelClass(value) : "Any";
    case "visibility":
      return !!value ? `${formatTitleCase(value)} only` : "All";
    case "year":
      return getYearValue(value, sliderDefaults?.year) || "Any";
    default:
      return "Any";
  }
}

function getAboveBelowRangeValue(value, defaults) {
  const [min, max, text = ""] =
    getRangeValues({
      value,
      defaults,
      maxOnlyText: " and below",
      minOnlyText: " and above",
    }) || [];

  if (min !== null && max !== null) {
    return `between ${formatNumber(min)} and ${formatNumber(max)}${text}`;
  } else if (min !== null || max !== null) {
    return `${formatNumber(min || max)}${text}`;
  } else {
    return null;
  }
}

function getLocationZipValue(zip, distance = SITE_DISTANCE_OPTIONS[0].value) {
  return !!zip
    ? `Within ${distance}${distance === 200 ? "+" : ""} miles of ${zip}`
    : null;
}

function getMeterValue(value, defaults) {
  const [min, max, text = ""] =
    getRangeValues({
      value,
      defaults,
      maxOnlyText: " or less",
      minOnlyText: " or more",
    }) || [];

  let { type = "meter" } = defaults || {};

  if ((min !== null || max !== null) && !!type) {
    type = ` ${type}`;
  }

  if (min !== null && max !== null) {
    return `${formatNumber(min)} – ${formatNumber(max)}${type}${text}`;
  } else if (min !== null || max !== null) {
    return `${formatNumber(min || max)}${type}${text}`;
  } else {
    return null;
  }
}

function getPriceValue(value, defaults) {
  const [min, max, text = ""] =
    getRangeValues({
      value,
      defaults,
      maxOnlyText: " or less",
      minOnlyText: " or more",
    }) || [];

  if (min !== null && max !== null) {
    return `$${formatNumber(min)} – $${formatNumber(max)}${text}`;
  } else if (min !== null || max !== null) {
    return `$${formatNumber(min || max)}${text}`;
  } else {
    return null;
  }
}

function getRangeValues({
  value,
  defaults,
  maxOnlyText = " or less",
  minOnlyText = " or greater",
}) {
  try {
    if (!value && !defaults) {
      throw new Error("Missing values and defaults");
    }

    let { min: dfMin, max: dfMax, defaultValues: fb } = defaults || {};

    if (
      !value &&
      Array.isArray(fb) &&
      ((!dfMin && dfMin !== 0) || dfMin === fb[0]) &&
      (!dfMax || dfMax === fb[1])
    ) {
      return [fb[0], fb[1], " (Default)"];
    } else if (!value && (!!dfMin || dfMin === 0) && !!dfMax) {
      return [dfMin, dfMax, " (Any)"];
    }

    if (!!value && Array.isArray(fb) && !dfMin && dfMin !== 0) {
      dfMin = fb[0];
    }

    if (!!value && Array.isArray(fb) && !dfMax) {
      dfMax = fb[1];
    }

    let [min, max] = parseRangeQuery(value);

    if (isNaN(min) || isNaN(max)) {
      throw new Error("Invalid value");
    }

    if (max === -1) {
      max = dfMax;
    }

    if (min === dfMin && max < dfMax) {
      return [null, max, maxOnlyText];
    }

    if (min > dfMin && max === dfMax) {
      return [min, null, minOnlyText];
    }

    if (min !== dfMin || max !== dfMax) {
      return min === max ? [min] : [min, max];
    }

    throw new Error("Unrecognized value");
  } catch (err) {
    return null;
  }
}

function getSortValue(value) {
  const selected =
    !!value && SITE_SORT_OPTIONS.find(option => option.value === value);

  if (!!selected?.label) {
    return selected.label;
  } else {
    return `${SITE_SORT_OPTIONS[0].label} (Default)`;
  }
}

function getYearValue(value, defaults) {
  const [min, max, text = ""] =
    getRangeValues({
      value,
      defaults,
      maxOnlyText: " or older",
      minOnlyText: " or newer",
    }) || [];

  if (min !== null && max !== null) {
    return `${min} – ${max}${text}`;
  } else if (min !== null || max !== null) {
    return `${min || max}${text}`;
  } else {
    return null;
  }
}
